// app/javascript/controllers/push_notifications_controller.js
// Handles push notification subscription and management
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["enableButton", "disableButton", "status", "unsupportedMessage"]
  static values = {
    enabled: Boolean
  }

  connect() {
    this.checkSupport()
    this.updateUI()
  }

  checkSupport() {
    this.supported = 'serviceWorker' in navigator &&
                     'PushManager' in window &&
                     'Notification' in window
  }

  async enable() {
    if (!this.supported) {
      this.showStatus("Push notifications are not supported on this device", "error")
      return
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        this.showStatus("Permission denied. Please allow notifications in your browser settings.", "error")
        return
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready

      // Get VAPID public key from server
      const vapidKey = await this.getVapidKey()

      if (!vapidKey) {
        this.showStatus("Push notifications are not configured", "error")
        return
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
      })

      // Save subscription to server
      await this.saveSubscription(subscription)

      this.enabledValue = true
      this.updateUI()
      this.showStatus("Push notifications enabled!", "success")

    } catch (error) {
      console.error('[Push] Enable failed:', error)
      this.showStatus("Failed to enable notifications: " + error.message, "error")
    }
  }

  async disable() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        // Delete from server first
        await this.deleteSubscription(subscription)
        // Then unsubscribe locally
        await subscription.unsubscribe()
      }

      this.enabledValue = false
      this.updateUI()
      this.showStatus("Push notifications disabled", "info")

    } catch (error) {
      console.error('[Push] Disable failed:', error)
      this.showStatus("Failed to disable notifications", "error")
    }
  }

  async getVapidKey() {
    try {
      const response = await fetch('/api/push_subscriptions/vapid_public_key')
      if (!response.ok) {
        throw new Error('VAPID key not available')
      }
      const data = await response.json()
      return data.publicKey
    } catch (error) {
      console.error('[Push] Failed to get VAPID key:', error)
      return null
    }
  }

  async saveSubscription(subscription) {
    const json = subscription.toJSON()

    const response = await fetch('/api/push_subscriptions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({
        endpoint: json.endpoint,
        p256dh_key: json.keys.p256dh,
        auth_key: json.keys.auth
      })
    })

    if (!response.ok) {
      throw new Error('Failed to save subscription')
    }
  }

  async deleteSubscription(subscription) {
    const response = await fetch('/api/push_subscriptions', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    })

    if (!response.ok && response.status !== 404) {
      throw new Error('Failed to delete subscription')
    }
  }

  // Convert VAPID public key from base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  async updateUI() {
    if (!this.supported) {
      this.showUnsupported()
      return
    }

    // Check if already subscribed
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      this.enabledValue = !!subscription
    } catch (error) {
      this.enabledValue = false
    }

    // Update button visibility
    if (this.hasEnableButtonTarget && this.hasDisableButtonTarget) {
      if (this.enabledValue) {
        this.enableButtonTarget.classList.add('hidden')
        this.disableButtonTarget.classList.remove('hidden')
      } else {
        this.enableButtonTarget.classList.remove('hidden')
        this.disableButtonTarget.classList.add('hidden')
      }
    }

    // Hide unsupported message
    if (this.hasUnsupportedMessageTarget) {
      this.unsupportedMessageTarget.classList.add('hidden')
    }
  }

  showUnsupported() {
    if (this.hasEnableButtonTarget) {
      this.enableButtonTarget.classList.add('hidden')
    }
    if (this.hasDisableButtonTarget) {
      this.disableButtonTarget.classList.add('hidden')
    }
    if (this.hasUnsupportedMessageTarget) {
      this.unsupportedMessageTarget.classList.remove('hidden')
    }
  }

  showStatus(message, type) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove('hidden', 'text-green-600', 'text-red-600', 'text-blue-600')

      switch (type) {
        case 'success':
          this.statusTarget.classList.add('text-green-600')
          break
        case 'error':
          this.statusTarget.classList.add('text-red-600')
          break
        case 'info':
          this.statusTarget.classList.add('text-blue-600')
          break
      }

      // Clear status after 5 seconds
      setTimeout(() => {
        if (this.hasStatusTarget) {
          this.statusTarget.classList.add('hidden')
        }
      }, 5000)
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
